import $ from 'jquery';
import {
    getSetting,
    isFieldValid,
    setFieldValue,
    getFieldValue
} from "@wc-stripe/utils";
import {omit, isEmpty, isEqual} from 'lodash';

/**
 * Checkout Field Populator Helper
 *
 * Handles population of WooCommerce checkout fields from payment data.
 * Context-aware: behaves differently for checkout vs cart/product pages.
 */
class CheckoutFields {
    /**
     * @param {string} context - Page context ('checkout', 'cart', 'product')
     * @param {BaseController} controller - Controller instance for accessing validation methods
     */
    constructor(context, controller) {
        this.context = context;
        this.controller = controller;
    }

    /**
     * Populate checkout fields from standardized payment data
     * @param {Object} data - Payment data containing billing/shipping info
     * @param {Object} options - Population options
     * @param {boolean} options.needsShipping - Whether shipping is needed
     */
    populate(data, options = {}) {
        // Sync current DOM values to Map before processing payment data
        // Only needed for checkout/express checkout context where we validate against existing fields
        if (this.context.isCheckout() || this.context.isExpressCheckout()) {
            this.syncDOMToMap(options);
        }

        if (data?.billing && !options?.skipBilling) {
            this.populateBilling(data.billing, options);
        }

        if (data?.shipping && !options.skipShipping && options.needsShipping) {
            this.populateShipping(data.shipping, options);
        }
    }

    /**
     * Sync current DOM field values to the fields Map
     * This ensures the Map reflects the current checkout state before making validation decisions
     * @param {Object} options - Population options
     */
    syncDOMToMap(options = {}) {
        const billingFields = ['first_name', 'last_name', 'company', 'address_1', 'address_2',
            'city', 'state', 'postcode', 'country', 'email', 'phone'];

        // Sync billing fields
        billingFields.forEach(field => {
            const $element = $(`[name="billing_${field}"]`);
            if ($element.length) {
                setFieldValue(field, $element.val(), 'billing');
            }
        });

        // Sync shipping fields if needed
        if (options.needsShipping) {
            const shippingFields = ['first_name', 'last_name', 'company', 'address_1', 'address_2',
                'city', 'state', 'postcode', 'country', 'phone'];

            shippingFields.forEach(field => {
                const $element = $(`[name="shipping_${field}"]`);
                if ($element.length) {
                    setFieldValue(field, $element.val(), 'shipping');
                }
            });
        }
    }

    /**
     * Populate billing address fields
     * @param {Object} billing - Billing data (email, phone, address fields)
     * @param {Object} options - Population options
     */
    populateBilling(billing, options) {
        const {email, phone, ...address} = billing;

        // Context-aware address population
        if (!isEmpty(address)) {
            if (this.context.isCheckout() || this.context.isExpressCheckout()) {
                // Checkout: validate before populating
                if (this.isValidAddress(address, ['first_name', 'last_name', 'phone']) &&
                    !isEqual(
                        this.getCartFullAddress('billing'),
                        omit(address, ['first_name', 'last_name'])
                    )) {
                    this.controller._updateRequired = true;
                    this.populateAddressFields(address, 'billing');

                    // Re-populate after updated_checkout. The reason for this is if
                    // the billing_country changed, the billing_state value might not be populated in the DOM.
                    $(document.body).one('updated_checkout', () => {
                        this.populateAddressFields(address, 'billing');
                    });
                }
            } else {
                // Cart/Product: simple population
                this.populateAddressFields(address, 'billing');
            }
        }

        // Email population (checkout checks validity first)
        if (email) {
            if (this.context.isCheckout()) {
                if (!isFieldValid('billing_email')) {
                    setFieldValue('billing_email', email);
                }
            } else {
                setFieldValue('billing_email', email);
            }
        }

        // Phone population (populate both billing and shipping)
        if (phone) {
            setFieldValue('billing_phone', phone);
            if (options.needsShipping) {
                setFieldValue('shipping_phone', phone);
            }
        }
    }

    /**
     * Populate shipping address fields
     * @param {Object} shipping - Shipping data (phone, address fields)
     * @param {Object} options - Population options
     */
    populateShipping(shipping, options) {
        const {phone, ...address} = shipping;

        if (!isEmpty(address)) {
            if (this.context.isCheckout() || this.context.isExpressCheckout()) {
                // Checkout: track changes and defer updates
                const cartAddress = this.getCartFullAddress('shipping');
                if (!isEqual(cartAddress, address)) {
                    this.controller._updateRequired = true;
                    this.populateAddressFields(address, 'shipping');

                    // Re-populate after updated_checkout. The reason for this is if
                    // the shipping_country changed, the shipping_state value might not be populated in the DOM.
                    $(document.body).one('updated_checkout', () => {
                        this.populateAddressFields(address, 'shipping');
                    });
                }

                // Backfill billing if needed (checkout-specific validation)
                if (!this.isValidAddress(this.getCartFullAddress('billing'), ['phone', 'email'])) {
                    // Only populate billing name if both first and last name are invalid
                    if (address.first_name && address.last_name &&
                        !isFieldValid('billing_first_name') && !isFieldValid('billing_last_name')) {
                        setFieldValue('first_name', address.first_name, 'billing');
                        setFieldValue('last_name', address.last_name, 'billing');
                    }
                    this.populateAddressFields(address, 'billing');
                }
            } else {
                // Cart/Product: simple population
                this.populateAddressFields(address, 'shipping');

                // Simpler backfill for cart/product
                if (!this.isValidAddress(this.getCartFullAddress('billing'), ['phone', 'email'])) {
                    this.populateAddressFields(address, 'billing');
                }
            }
        }

        // Populate shipping phone
        if (phone) {
            setFieldValue('shipping_phone', phone);
        }

        // Checkout-specific: ship to different address logic
        if (this.context.isCheckout() || this.context.isExpressCheckout()) {
            this.maybeShipToDifferentAddress();
        }
    }

    maybeShipToDifferentAddress() {
        if ($('[name="ship_to_different_address"]')?.length) {
            const billingAddress = this.getCartFullAddress('billing');
            const shippingAddress = this.getCartFullAddress('shipping');
            const bool = !isEqual(
                billingAddress,
                shippingAddress
            )
            $('[name="ship_to_different_address"]').prop('checked', bool).trigger('change');
        }
    }

    /**
     * Populate address fields for a given prefix (billing/shipping)
     * @param {Object} address - Address data
     * @param {string} prefix - Field prefix ('billing' or 'shipping')
     */
    populateAddressFields(address, prefix) {
        for (let key in address) {
            setFieldValue(key, address[key], prefix);
        }
    }

    getCartFullAddress(type) {
        return {
            first_name: getFieldValue('first_name', type),
            last_name: getFieldValue('last_name', type),
            address_1: getFieldValue('address_1', type),
            address_2: getFieldValue('address_2', type),
            state: getFieldValue('state', type),
            city: getFieldValue('city', type),
            postcode: getFieldValue('postcode', type),
            country: getFieldValue('country', type)
        }
    }

    /**
     * Validate if an address has all required fields populated based on country locale
     *
     * Uses WooCommerce address locale configuration to determine which fields are required
     * for a given country. Each country has different address requirements (e.g., postcode
     * is required in US but not in some other countries).
     *
     * @param {Object} address - Address object to validate (must contain 'country' property)
     * @param {Array<string>} exclude - Field keys to exclude from validation (e.g., ['first_name', 'phone'])
     * @returns {boolean} True if all required fields are populated, false otherwise
     *
     * @example
     * // Validate billing address but skip first_name, last_name, phone validation
     * isValidAddress(billingAddress, ['first_name', 'last_name', 'phone'])
     */
    isValidAddress(address, exclude = []) {
        // Lazy load and cache address locales (only parsed once)
        if (!this.locales) {
            this.locales = JSON.parse(getSetting('addressLocales').replace(/&quot;/g, '"'));
        }

        // Country is required - can't validate without it
        if (!address.country || isEmpty(address.country)) {
            return false;
        }

        // Get country-specific locale, fallback to default if country not found
        let locale = this.locales[address.country] || this.locales.default;

        // Merge default locale with country-specific locale
        // Country-specific settings override defaults (e.g., postcode.required = false for IE)
        locale = {...this.locales.default, ...locale};

        // Build entries array and filter out excluded fields
        // Each entry is [key, {required: boolean, ...}] e.g., ['postcode', {required: true}]
        const entries = Object.entries(locale).filter(([key, value]) => !exclude.includes(key));

        // Validate each required field
        for (let [key, obj] of entries) {
            if (obj.required) {
                const value = address?.[key] || null;
                // Fail if field is missing or empty (after trimming whitespace)
                if (!value?.trim()) {
                    return false;
                }
            }
        }

        // All required fields are populated
        return true;
    }
}

export default CheckoutFields;